import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import React from 'react';
import ReactDOM from 'react-dom/client';
import outputs from '../amplify_outputs.json';
import App from './App';
import './index.css';
import { initTelemetry, addDeviceContext, ErrorBoundary } from './services/telemetry';
import { initializeDeepLinking } from './services/deepLinking';

// Initialize error tracking and performance monitoring
initTelemetry();

// Initialize native features (deep linking, device context)
async function initializeNativeFeatures(): Promise<void> {
  try {
    // Initialize deep linking for native apps
    await initializeDeepLinking();

    // Add device context to Sentry for better error reporting
    await addDeviceContext();

    // Native features initialized
  } catch (error) {
    // Failed to initialize some native features: error
  }
}

// Start native initialization (non-blocking)
initializeNativeFeatures();

// Configure Amplify with generated outputs from sandbox/deploy
// Cast outputs to add allowGuestAccess for unauthenticated Identity Pool credentials
// This is needed for IAM auth mode to work without being signed in
const configWithGuestAccess = {
  ...outputs,
  auth: outputs.auth
    ? {
        ...outputs.auth,
        allowGuestAccess: true,
      }
    : undefined,
};
Amplify.configure(configWithGuestAccess as typeof outputs);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              We've been notified and are working to fix this.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
            >
              Reload Page
            </button>
          </div>
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
